import { DialogComponent } from '@theme/dialog';

const STORAGE_KEY = 'olori:signup-popup';

/**
 * First-visit email capture.
 *
 * Extends Horizon's DialogComponent so focus trapping, scroll locking and ESC-to-close
 * come from the theme rather than being reimplemented here.
 *
 * Deliberately quiet: it fires once after a delay, never re-opens for someone who has
 * dismissed or subscribed, and stays shut entirely in the theme editor and for anyone
 * who prefers reduced motion being irrelevant — the point is it must not become the
 * intrusive interstitial pattern Google penalises on mobile.
 */
class OloriSignupPopup extends DialogComponent {
  #timer = null;

  connectedCallback() {
    super.connectedCallback();

    // Never auto-open inside the theme editor; it would fight the merchant on every
    // save. Shopify exposes Shopify.designMode on the storefront in the editor.
    if (window.Shopify?.designMode) return;

    if (this.#alreadyHandled()) return;

    const delay = Number(this.dataset.delay ?? 15) * 1000;
    this.#timer = window.setTimeout(() => this.#open(), delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) window.clearTimeout(this.#timer);
  }

  #alreadyHandled() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      const { until } = JSON.parse(raw);
      // A dismissal is a snooze, not a permanent no — except after subscribing.
      return until === 'forever' || Date.now() < until;
    } catch {
      // Private browsing or storage disabled: fail closed so we never nag.
      return true;
    }
  }

  #remember(scope) {
    try {
      const days = Number(this.dataset.snoozeDays ?? 30);
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ until: scope === 'forever' ? 'forever' : Date.now() + days * 864e5 })
      );
    } catch {
      /* storage unavailable — nothing to remember, popup simply may show again */
    }
  }

  #open() {
    if (!this.isConnected) return;
    this.showDialog();
  }

  /** Close and snooze. Wired to the close button and the backdrop. */
  dismiss = () => {
    this.#remember('snooze');
    this.closeDialog();
  };
}

if (!customElements.get('olori-signup-popup')) {
  customElements.define('olori-signup-popup', OloriSignupPopup);
}

/**
 * After a successful Shopify customer-form POST the page reloads with ?customer_posted=true.
 * Mark the visitor as subscribed so the popup never returns, and reopen it once to show
 * the confirmation.
 */
if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ until: 'forever' }));
  } catch {
    /* ignore */
  }
}
